import React, { useCallback, useState } from 'react';
import Select from "react-select";

import {
    adminUpdateAccountAdminFeatures,
} from '../../core/_requests';
import { APIError, useAxios } from '../../core/_axios';
import { Account } from '../../core/_models';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { useAgencies } from '../../hooks/useAgencies';
import { AccountFast } from '../../core/_models_fast';
import useLocalizedCopy from '../../hooks/useLocalizedCopy';
import AccountEmailSettings from '../../components/AccountEmailSettings';

export default function AccountAdminForm({
    account,
    setAccount
}: {
    account: Account,
    setAccount: (account: Account) => void;
}) {
    const axios = useAxios();
    const navigate = useNavigate();
    const { copy } = useLocalizedCopy();

    const {
        agencies: allAgencies,
        loading: loadingAgencies,
        loadingError: loadingAgenciesError,
        reload: loadAgencies
    } = useAgencies();

    const [saving, setSaving] = useState(false);
    const [errorSaving, setErrorSaving] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

    const onManageAccountFormSubmitted = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (saving) {
            return;
        }
        setSaveSuccess(null);
        setErrorSaving(null);
        setSaving(true);
        const form = event.currentTarget;
        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());
        const finalData = {
            account_unique_id: account.unique_identifier,
            form_data: {
                ...data,
                belongs_to_agency_unique_identifier: data.belongs_to_agency_unique_identifier || null,
                is_demo_account: data.is_demo_account === 'on' ? true : false,
            }
        }
        console.log('Final Data:', finalData);
        try {
            const updatedAccount = await adminUpdateAccountAdminFeatures(axios, finalData);
            setAccount(updatedAccount);
            setSaveSuccess(copy.Account.details.updated);
        } catch (exception) {
            console.error(exception);
            const errorMessage = exception instanceof APIError ? exception.message : copy.Errors.generic + '. ' + copy.Common.messages.tryAgain;
            setErrorSaving(errorMessage);
        } finally {
            setSaving(false);
        }
    }, [saving, account, setAccount, axios, navigate]);

    return (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 lg:gap-7.5">
            <div className="col-span-2">
                <div className="flex flex-col gap-5 lg:gap-7.5">
                    <form className="card" onSubmit={onManageAccountFormSubmitted}>
                        <div className="card-header">
                            <h3 className="card-title">
                                {copy.Admin.title} {copy.Account.details.title}
                            </h3>
                        </div>
                        <div className="card-body grid gap-7.5 py-5 lg:py-7.5">
                            <div>
                                <label
                                    htmlFor="belongs_to_agency"
                                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    {copy.Admin.viewAgencies} ({copy.Common.labels.optional})
                                </label>
                                <Select
                                    inputId="belongs_to_agency"
                                    name="belongs_to_agency_unique_identifier"
                                    className="w-full"
                                    classNamePrefix="select"
                                    isClearable
                                    isLoading={loadingAgencies}
                                    options={allAgencies?.map(agency => ({
                                        value: agency.unique_identifier,
                                        label: agency.name,
                                    })) || []}
                                    defaultValue={
                                        account.belongs_to_agency
                                            ? {
                                                value: account.belongs_to_agency.unique_identifier,
                                                label: account.belongs_to_agency.name,
                                            }
                                            : null
                                    }
                                    placeholder={`${copy.Common.labels.select} ${copy.Admin.viewAgencies}...`}
                                />
                                {loadingAgenciesError && (
                                    <div className="text-red-500 text-sm mt-2">
                                        {copy.Errors.generic}
                                    </div>
                                )}
                            </div>
                            <div>
                                <input
                                    id="is_demo_account"
                                    name="is_demo_account"
                                    type="checkbox"
                                    className="mr-2"
                                    defaultChecked={!!account?.is_demo_account}
                                />
                                <label htmlFor="is_demo_account" className="text-sm font-medium text-gray-900 dark:text-white">
                                    Demo {copy.Common.navigation.account}
                                </label>
                            </div>
                        </div>
                        <div className="card-footer py-3.5 flex flex-col lg:flex-row items-center justify-between">
                            <div>
                                {errorSaving && <div className="text-red-500 text-sm font-medium">{errorSaving}</div>}
                                {saveSuccess && <div className="text-green-500 text-sm font-medium">{saveSuccess}</div>}
                            </div>
                            <div>
                                <button
                                    type="submit"
                                    className="btn btn-sm btn-primary"
                                    disabled={saving}
                                >
                                    {!saving ? copy.Account.details.update : copy.Account.details.updating}
                                </button>
                            </div>
                        </div>
                    </form>
                    {/* Email Settings Card */}
                    <div className="mt-5">
                        <AccountEmailSettings 
                            account={account} 
                            onUpdate={() => {
                                // Reload account to reflect changes
                                setSaveSuccess('Email settings updated successfully');
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
